/**
 * Drawer — an edge-anchored panel.
 *
 * Used by: the Admin mobile navigation, the product quick-editor beside the
 * list, the order detail panel, the POS modifier picker and the Staff task
 * detail sheet.
 *
 * Why a Drawer instead of a Modal: a drawer keeps the list visible behind it,
 * which matters when a manager is stepping through twenty products in a row.
 *
 * Accessibility mirrors Modal: `role="dialog"`, `aria-modal`, Escape to close,
 * focus moved in on open and restored on close, backdrop click closes.
 * On phones `side="bottom"` gives the familiar sheet.
 */
import { useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { cn } from '../utils.js';
import { IconButton } from './IconButton.js';
import { CloseGlyph } from './Modal.js';

export type DrawerSide = 'right' | 'left' | 'bottom';

export interface DrawerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly side?: DrawerSide;
  /** Tailwind width/height class for the panel. */
  readonly sizeClassName?: string;
  readonly footer?: ReactNode;
  readonly dismissible?: boolean;
  readonly className?: string;
  readonly children: ReactNode;
}

const PLACEMENT: Record<DrawerSide, string> = {
  right: 'right-0 top-0 h-full animate-slide-in-right border-l',
  left: 'left-0 top-0 h-full animate-slide-in-right border-r',
  bottom: 'bottom-0 left-0 w-full animate-slide-up rounded-t-xl border-t',
};

const DEFAULT_SIZE: Record<DrawerSide, string> = {
  right: 'w-full max-w-lg',
  left: 'w-full max-w-xs',
  bottom: 'max-h-[85vh]',
};

export function Drawer({
  open,
  onClose,
  title,
  description,
  side = 'right',
  sizeClassName,
  footer,
  dismissible = true,
  className,
  children,
}: DrawerProps): React.JSX.Element | null {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descId = useId();

  const requestClose = useCallback(() => {
    if (dismissible) onClose();
  }, [dismissible, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    previouslyFocused.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panelRef.current?.focus();

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        requestClose();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = overflow;
      previouslyFocused.current?.focus();
    };
  }, [open, requestClose]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-drawer">
      <div className="absolute inset-0 animate-fade-in bg-overlay/50" onClick={requestClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description !== undefined ? descId : undefined}
        tabIndex={-1}
        className={cn(
          'absolute flex flex-col bg-surface shadow-pop border-line outline-none',
          PLACEMENT[side],
          sizeClassName ?? DEFAULT_SIZE[side],
          className,
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-line px-4 py-3 sm:px-5">
          <div className="min-w-0">
            <h2 id={titleId} className="truncate text-base font-semibold text-fg">
              {title}
            </h2>
            {description !== undefined ? (
              <p id={descId} className="mt-0.5 text-sm text-fg-muted">
                {description}
              </p>
            ) : null}
          </div>
          {dismissible ? <IconButton label="Đóng" onClick={onClose} icon={<CloseGlyph />} size="sm" /> : null}
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5">{children}</div>

        {footer !== undefined ? (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line bg-surface-alt/60 px-4 py-3 sm:px-5">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
